import { create } from 'zustand';

import { getStreamPublisherPort } from './container';
import type {
  StreamPublisherState,
  StreamPublisherStats,
} from './ports/StreamPublisherPort';
import type {
  StreamLifecycleState,
  RecordingState,
  VirtualCameraState,
  ConnectionStatus,
  StreamMetrics,
} from '@shared/types';
import { localRecorderService } from './recorderService';

interface StreamPublisherStoreState {
  lifecycle: StreamLifecycleState;
  recording: RecordingState;
  virtualCamera: VirtualCameraState;
  connection: ConnectionStatus;
  metrics: StreamMetrics;
}

interface StreamPublisherStoreActions {
  startStream: (localStream: MediaStream) => Promise<void>;
  stopStream: () => Promise<void>;
  toggleRecording: () => void;
  toggleVirtualCamera: () => void;
  setConnection: (patch: Partial<ConnectionStatus>) => void;
  setMetrics: (patch: Partial<StreamMetrics>) => void;
}

type StreamPublisherStore = StreamPublisherStoreState & StreamPublisherStoreActions;

const streamPublisher = getStreamPublisherPort();

function mapPublisherState(state: StreamPublisherState): StreamLifecycleState {
  switch (state) {
    case 'idle':
      return 'offline';
    case 'streaming':
      return 'live';
    default:
      return state;
  }
}

function mapPublisherStats(stats: StreamPublisherStats): Partial<StreamMetrics> {
  return {
    fps: stats.fps ?? 0,
    latencyMs: stats.latencyMs ?? 0,
  };
}

/**
 * UI-facing store wrapping the StreamPublisherPort.
 *
 * Holds presentation state (lifecycle/recording/virtualCamera/connection/metrics)
 * for the Preview tab — Start/Stop/Record/Virtual Camera controls and the
 * StreamStatusBar. Transport work is delegated to the port, so the store
 * stays a thin reactive layer on top of the streaming feature's adapters.
 */
export const useStreamPublisherStore = create<StreamPublisherStore>((set) => ({
  lifecycle: 'offline',
  recording: 'idle',
  virtualCamera: 'disabled',
  connection: {
    cameraConnected: false,
    microphoneConnected: false,
    streamConnected: false,
    obsConnected: false,
    virtualCamera: 'disabled',
  },
  metrics: { latencyMs: 0, fps: 0 },

  startStream: async (localStream) => {
    set((state) => ({
      lifecycle: 'starting',
      connection: { ...state.connection, cameraConnected: true, microphoneConnected: true },
    }));
    try {
      await streamPublisher.start(localStream);
    } catch (error) {
      set((state) => ({
        lifecycle: 'error',
        connection: { ...state.connection, cameraConnected: false, microphoneConnected: false },
      }));
      throw error;
    }
  },
  stopStream: async () => {
    set({ lifecycle: 'stopping' });
    try {
      await streamPublisher.stop();
    } catch (error) {
      set({ lifecycle: 'error' });
      throw error;
    }
  },

  toggleRecording: () => {
    const currentRecState = localRecorderService.getState();
    if (currentRecState === 'idle') {
      const session = streamPublisher.getSession();
      if (!session) {
        console.warn('Cannot start recording: no active publishing session');
        return;
      }
      localRecorderService.start(session.localStream);
    } else if (currentRecState === 'recording') {
      localRecorderService.stop();
    }
  },

  toggleVirtualCamera: () =>
    set((state) => ({
      virtualCamera: state.virtualCamera === 'disabled' ? 'enabled' : 'disabled',
    })),
  setConnection: (patch) =>
    set((state) => ({ connection: { ...state.connection, ...patch } })),
  setMetrics: (patch) => set((state) => ({ metrics: { ...state.metrics, ...patch } })),

}));

streamPublisher.onStateChanged((publisherState) => {
  useStreamPublisherStore.setState((state) => {
    const lifecycle = mapPublisherState(publisherState);
    const isPublishing = publisherState === 'streaming';
    const isOffline = publisherState === 'idle';

    return {
      lifecycle,
      connection: {
        ...state.connection,
        cameraConnected: isOffline ? false : state.connection.cameraConnected,
        microphoneConnected: isOffline ? false : state.connection.microphoneConnected,
        streamConnected: isPublishing,
      },
      metrics: isOffline ? { latencyMs: 0, fps: 0 } : state.metrics,
    };
  });
});

streamPublisher.onStatsChanged((stats) => {
  useStreamPublisherStore.setState((state) => ({
    metrics: {
      ...state.metrics,
      ...mapPublisherStats(stats),
    },
  }));
});

localRecorderService.onStateChanged((recState) => {
  useStreamPublisherStore.setState({ recording: recState });
});
